import { DocumentVerificationDTO } from '../../core/view-states/document-verification-view.service.js';
import { DocumentVerificationUpdateRTM } from '@gains-com/realtime';
import type { DocumentVerificationStatus } from './document-verification-dialog.types.js';
import {
  DocumentVerificationTypeId,
  TargetEntityQuery,
} from '@gains-com/shared';
import { getCreditCardPaymentMethodsForDisplay } from '../../core/payments-common.js';

// Re-export for consumers that were importing from this file
export { getCreditCardPaymentMethodsForDisplay };

/**
 * Derives composite status from ALL matching verifications.
 *
 * Logic:
 * - If no verifications or all are 'created' → 'start'
 * - If ANY is approved → 'approved'
 * - If ANY is pending, evaluating, or blocked → 'pending'
 * - If ANY is rejected OR (finalized && !approved) → 'rejected'
 * - Fallback → 'pending'
 */
export function deriveStatusFromVerifications(
  verifications: (DocumentVerificationDTO | DocumentVerificationUpdateRTM)[]
): DocumentVerificationStatus {
  // No active verifications or all are 'created' -> start
  if (verifications.length === 0) return 'pending';
  if (verifications.every((v) => v.status === 'created')) return 'pending';

  // If ANY is approved -> approved
  if (verifications.some((v) => v.approved || v.status === 'approved')) {
    return 'approved';
  }

  const pendingOrEvaluating = verifications.filter(
    (v) => v.status === 'pending' || v.status === 'evaluating'
  );

  // If ANY is pending or evaluating -> pending
  if (pendingOrEvaluating.length) {
    return 'pending';
  }

  // If ANY is rejected OR (finalized && !approved) -> rejected
  if (
    verifications.some(
      (v) => v.status === 'rejected' || (v.finalized && !v.approved)
    )
  ) {
    return 'rejected';
  }

  // Fallback to pending
  return 'pending';
}

/**
 * Converts query inputs to a target entity if all conditions are met.
 *
 * This method determines whether we should immediately link the uploaded documents to a
 * specific target entity, or instead query for matching verifications after upload.
 *
 * **Decision Logic**:
 * - Returns a target entity (for immediate linking) ONLY if:
 *   1. All three query inputs are provided (targetEntityId, targetEntityTypeId, documentVerificationTypeId)
 *   2. Each input is an array with exactly one element
 *   3. That single element is non-null
 *
 * **Use Cases**:
 * - **Returns Target Entity**: When you know the exact target you're verifying
 *   (e.g., verifying a specific payment method with known ID)
 *   → Backend immediately links the document to that target upon processing
 *
 * - **Returns Null**: When any condition fails
 *   → Backend does NOT link immediately
 *   → Dialog will query for matching verifications after upload
 *   → Useful for "any matching target" scenarios (e.g., any Coinflow card)
 *
 * @param query - The query object containing targetEntityId, targetEntityTypeId, and documentVerificationTypeId arrays
 * @returns Target entity object if all conditions met, null otherwise
 */
export function makeTargetEntity(query?: TargetEntityQuery | null) {
  const { targetEntityId, targetEntityTypeId, documentVerificationTypeId } =
    query ?? {};

  // Check if we have exactly one specific target to link to
  // All three conditions must be met for immediate linking:
  // 1. Each property must be an array
  // 2. Each array must have exactly one element
  // 3. That element must not be null
  if (
    Array.isArray(targetEntityId) &&
    targetEntityId.length === 1 &&
    targetEntityId[0] !== null &&
    Array.isArray(targetEntityTypeId) &&
    targetEntityTypeId.length === 1 &&
    targetEntityTypeId[0] !== null &&
    Array.isArray(documentVerificationTypeId) &&
    documentVerificationTypeId.length === 1 &&
    documentVerificationTypeId[0] !== null
  ) {
    // All conditions met: return target for immediate linking
    return {
      targetEntityId: targetEntityId[0],
      targetEntityTypeId: targetEntityTypeId[0],
      documentVerificationTypeId: documentVerificationTypeId[0],
    };
  }

  // Any condition failed: return null to use query-based matching instead
  return null;
}

/**
 * Creates a query object from the component's input properties.
 *
 * This method normalizes the input properties (which can be single values, arrays, or null)
 * into a consistent query format that the dialog uses to find matching document verifications.
 *
 * **Decision Logic**:
 * - Returns null if ALL three inputs are null/falsy
 *   → "uploadOnly" mode: no verification waiting needed
 *   → Useful when user has no target entity yet (e.g., never made a purchase)
 *
 * - Returns a query object if ANY input is provided
 *   → Dialog will wait for a verification matching the provided criteria
 *   → Query will be used by the computed `verifications` signal to find matches
 *
 * **Query Format**:
 * - All values are normalized to arrays (single values become single-element arrays)
 * - Only provided (non-null) inputs are included in the query
 * - The query supports flexible matching:
 *   - Single-element arrays: Match one specific value
 *   - Multi-element arrays: Match any of the provided values
 *
 * @returns Query object with array properties, or null if no inputs provided
 */
export function makeTargetEntityQuery({
  targetEntityTypeId,
  targetEntityId,
  documentVerificationTypeId,
}: {
  targetEntityTypeId?: string | string[] | null;
  targetEntityId?: string | string[] | null;
  documentVerificationTypeId?: string | string[] | null;
}): TargetEntityQuery | null {
  // If no inputs provided, return null (uploadOnly mode)
  if (!targetEntityTypeId && !targetEntityId && !documentVerificationTypeId) {
    return null;
  }

  // Build query object with normalized array values
  // Only include properties that are not null
  return {
    ...(typeof targetEntityTypeId !== 'undefined' && targetEntityTypeId !== null
      ? {
          targetEntityTypeId: Array.isArray(targetEntityTypeId)
            ? targetEntityTypeId
            : [targetEntityTypeId],
        }
      : {}),
    ...(typeof targetEntityId !== 'undefined' && targetEntityId !== null
      ? {
          targetEntityId: Array.isArray(targetEntityId)
            ? targetEntityId
            : [targetEntityId],
        }
      : {}),
    ...(typeof documentVerificationTypeId !== 'undefined' &&
    documentVerificationTypeId !== null
      ? {
          documentVerificationTypeId: Array.isArray(documentVerificationTypeId)
            ? documentVerificationTypeId
            : [documentVerificationTypeId],
        }
      : {}),
  } as TargetEntityQuery;
}

/**
 * Returns ALL verifications matching the target entity query.
 * Excludes failed verifications (permanently failed or status === 'failed').
 */
export function getVerificationsForTargetEntityQuery(
  query: TargetEntityQuery | null,
  verifications:
    | (DocumentVerificationDTO | DocumentVerificationUpdateRTM)[]
    | null
): (DocumentVerificationDTO | DocumentVerificationUpdateRTM)[] {
  if (!query) return [];

  const allVerifications = verifications ?? [];

  const { documentVerificationTypeId, targetEntityTypeId, targetEntityId } =
    query;

  return allVerifications.filter((v) => {
    // Exclude failed verifications entirely
    if (v.failed_permanently || v.status === 'failed') {
      return false;
    }

    // Match against query criteria
    const typeMatch =
      !targetEntityTypeId?.length ||
      targetEntityTypeId.includes(v.target_entity_type_id);
    const idMatch =
      !targetEntityId?.length || targetEntityId.includes(v.target_entity_id);
    const docTypeMatch =
      !documentVerificationTypeId?.length ||
      documentVerificationTypeId.includes(
        v.document_verification_type_id as DocumentVerificationTypeId
      );

    return typeMatch && idMatch && docTypeMatch;
  });
}

